import type React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { uuid } from '@sanity/uuid'

import { useUserPortfolio } from '~/containers/AppContainer/PortfolioProvider'
import type { PortfolioArtworkDetails } from '~/hooks/usAddToMyPortfolioActions'
import useIsolatedPortfolioActions from '~/hooks/useIsolatedPortfolioActions'
import type { PushProperties } from '~/hooks/useMabPostHog'
import { PushWebProduct } from '~/hooks/useMabPostHog'
import useMabPostHog, { PushInteractionType } from '~/hooks/useMabPostHog'
import usePortfolioInfoWindow from '~/hooks/usePortfolioInfoWindow'
import usePortfolioTicketUpdater from '~/hooks/usePortfolioTicketUpdater'
import { serializePortfolioArtworkData } from '~/services/serializePortfolioArtworkData'
import { isArtworkInPortfolio } from '~/utils/isArtworkInPortfolio'

import type { ImageProps } from '../../atoms/Image/Image'
import { PortfolioCollectionVariant } from '../PortfolioCollection/PortfolioCollection'
import { PortfolioItemDetailsTheme } from '../PortfolioItemDetails/PortfolioItemDetails'

interface UseArtworkCardButtonsReturn {
  isPortfolioTicket: number | null
  isWatchingTicket: number | null
  isSellingTicket: number | null
  isAddingPortfolio: boolean
  isAddingWatching: boolean
  isRemovingWatchlist: boolean

  handleRemoveArtwork: (e: React.MouseEvent) => void
  handleAddToWatchList: (e: React.MouseEvent) => void
  handleAddToPortfolio: (e: React.MouseEvent) => void
  handleContainerClick: (e: React.MouseEvent) => false

  handleViewPortfolioClick: (e: React.MouseEvent) => void
  handleBookmarkClick: (e: React.MouseEvent) => void
}

export const useArtworkCardButtons = (
  artworkId: string,
  artworkImage: ImageProps,
  artistId: string,
  eventLocation: string,
  artistName: string | undefined,
  artworkName: string | undefined,
  artworkDetails: PortfolioArtworkDetails | null
): UseArtworkCardButtonsReturn => {
  const {
    addToMyPortfolioHandler,
    addToWatchListHandler,
    isAddingPortfolio,
    isAddingWatching,
    openConfirmPortfolioActionModal,
  } = useIsolatedPortfolioActions(
    artworkId,
    artworkImage,
    artistId || '',
    artworkDetails
  )
  const { pushEvent } = useMabPostHog()
  const { openInfoWindow } = usePortfolioInfoWindow()

  const { portfolioDetails } = useUserPortfolio()
  const { doUpdate } = usePortfolioTicketUpdater()

  const [isRemovingWatchlist, setIsRemovingWatchlist] = useState(false)

  // track if actions are in progress for this specific artwork
  const processingAction = useRef<boolean>(false)

  const isPortfolioTicket = useMemo(
    () => isArtworkInPortfolio(artworkId, 'portfolio', portfolioDetails),
    [artworkId, portfolioDetails]
  )

  const isWatchingTicket = useMemo(
    () => isArtworkInPortfolio(artworkId, 'watching', portfolioDetails),
    [artworkId, portfolioDetails]
  )

  const isSellingTicket = useMemo(
    () => isArtworkInPortfolio(artworkId, 'selling', portfolioDetails),
    [artworkId, portfolioDetails]
  )
  const preventEventBubbling = useCallback((e: React.MouseEvent) => {
    if (e && e.stopPropagation) {
      e.stopPropagation()
      e.preventDefault()
    }
  }, [])

  const analyticsEventHandler = useCallback(
    (product: PushWebProduct, location: string) => {
      const payload: Partial<PushProperties> = {
        interactionType: PushInteractionType.Click,
        webProduct: product,
        interactionLocation: location,
        artistName,
        artworkName,
        formLaunchId: uuid(),
      }

      pushEvent('SUPER QUICKADD complete', payload as PushProperties)
    },
    [artistName, artworkName, pushEvent]
  )

  const handleRemoveArtwork = useCallback(
    (e: React.MouseEvent) => {
      preventEventBubbling(e)
      setIsRemovingWatchlist(true)

      // prevent multiple executions
      if (processingAction.current || !isWatchingTicket) return

      processingAction.current = true

      doUpdate(isWatchingTicket, { is_archived: true })

      setTimeout(() => {
        processingAction.current = false
        setIsRemovingWatchlist(false)
      }, 500)
    },
    [isWatchingTicket, doUpdate, preventEventBubbling]
  )

  const handleAddToWatchList = useCallback(
    (e: React.MouseEvent) => {
      preventEventBubbling(e)

      // prevent multiple executions
      if (processingAction.current || isAddingWatching) return

      processingAction.current = true

      addToWatchListHandler(e)

      analyticsEventHandler(PushWebProduct.WatchList, eventLocation)

      setTimeout(() => {
        processingAction.current = false
      }, 500)
    },
    [
      preventEventBubbling,
      isAddingWatching,
      addToWatchListHandler,
      analyticsEventHandler,
      eventLocation,
    ]
  )

  const handleAddToPortfolio = useCallback(
    (e: React.MouseEvent) => {
      preventEventBubbling(e)

      // prevent multiple executions
      if (processingAction.current || isAddingPortfolio) return

      processingAction.current = true

      addToMyPortfolioHandler(e)

      analyticsEventHandler(PushWebProduct.MyPortfoio, eventLocation)

      setTimeout(() => {
        processingAction.current = false
      }, 500)
    },
    [
      preventEventBubbling,
      isAddingPortfolio,
      addToMyPortfolioHandler,
      analyticsEventHandler,
      eventLocation,
    ]
  )

  const handleContainerClick = useCallback(
    (e: React.MouseEvent) => {
      preventEventBubbling(e)
      return false
    },
    [preventEventBubbling]
  )

  const handleViewPortfolioClick = useCallback(
    (e: React.MouseEvent) => {
      preventEventBubbling(e)
      e.preventDefault()

      const id = Number(artworkId.split('-')[1])

      const artworkItem = portfolioDetails?.portfolioItems?.portfolio.find(
        (artwork: { artwork_id: number }) => artwork.artwork_id === id
      )

      if (!artworkItem) {
        console.log('Artwork not found in portfolio')
        return
      }

      const portfolioItemDetails = serializePortfolioArtworkData(
        artworkItem,
        artworkDetails
      )

      openInfoWindow(
        portfolioItemDetails,
        PortfolioCollectionVariant['Myportfolio'],
        PortfolioItemDetailsTheme.Artwork,
        true
      )
    },
    [
      artworkDetails,
      artworkId,
      openInfoWindow,
      portfolioDetails?.portfolioItems?.portfolio,
      preventEventBubbling,
    ]
  )

  const trackedTicketId = isPortfolioTicket || isWatchingTicket

  // Removal stays in progress until the refreshed portfolio no longer
  // contains the ticket - doUpdate doesn't await its portfolio refresh, so
  // re-enabling earlier shows the tracked state (and remove X) again.
  useEffect(() => {
    if (isRemovingWatchlist && !trackedTicketId) {
      processingAction.current = false
      setIsRemovingWatchlist(false)
    }
  }, [isRemovingWatchlist, trackedTicketId])

  const handleRemoveTracked = useCallback(
    async (e: React.MouseEvent) => {
      preventEventBubbling(e)

      if (processingAction.current || !trackedTicketId) return

      processingAction.current = true
      setIsRemovingWatchlist(true)

      const result = await doUpdate(trackedTicketId, { is_archived: true })

      if (!result?.success) {
        processingAction.current = false
        setIsRemovingWatchlist(false)
      }
    },
    [trackedTicketId, doUpdate, preventEventBubbling]
  )

  const handleBookmarkClick = useCallback(
    (e: React.MouseEvent) => {
      if (isPortfolioTicket || isWatchingTicket) {
        void handleRemoveTracked(e)
        return
      }

      preventEventBubbling(e)

      const payload: Partial<PushProperties> = {
        interactionType: PushInteractionType.Click,
        webProduct: PushWebProduct.Sitewide,
        interactionLocation: eventLocation,
        artistName,
        artworkName,
      }

      pushEvent('Artwork track button clicked', payload as PushProperties)

      openConfirmPortfolioActionModal(
        'addtoportfolio',
        artworkName || 'Untitled',
        false,
        false
      )
    },
    [
      preventEventBubbling,
      isPortfolioTicket,
      isWatchingTicket,
      handleRemoveTracked,
      openConfirmPortfolioActionModal,
      artworkName,
    ]
  )

  return {
    isPortfolioTicket,
    isWatchingTicket,
    isSellingTicket,
    isAddingPortfolio,
    isAddingWatching,
    isRemovingWatchlist,

    handleRemoveArtwork,
    handleAddToWatchList,
    handleAddToPortfolio,
    handleContainerClick,

    handleViewPortfolioClick,
    handleBookmarkClick,
  }
}
